import {Button, Card, CardContent, Grid, InputAdornment, useTheme,} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useRecoilState} from "recoil";
import {chargingWayAtom} from "../../../state/constantsRecoil";
import {OWAncillaryEntityResponse} from "../../../api/types/owAncillary";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import CustomBanner from "../../../common/components/CustomBanner";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {
    ancillaryExceptionSetFormValues,
    fromGenericBeToGenericFe,
    fromOWAncillaryBeToFe,
    i18nConvertNumberWithDecimals,
    setFormNumberValue,
} from "../../../utils";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {unitsEnumAtom} from "../../../state/rentalPeriodsRecoil";
import {OWAncillaryExceptionBaseDefinitionFormValues} from "../../../api/types/owAncillaryException";
import {initialOWAncillaryExceptionBaseDefinitionValues} from "./OWAncillaryExceptionBaseDefinitionForm";
import {useOWAncillaryRepository} from "../../../api/repositories/OWAncillaryRepository";
import {useProviderRepository} from "../../../api/repositories/ProviderRepository";
import {CommercialSegmentSelectFE} from "../../../api/types/commercialSegment";
import {
    numberIntegerRegex,
    numberIntegerWithNegativeValuesRegex,
    numberWithNegativeValuesRegex,
} from "../../../constants";
import {AncillarySelectFE, ServiceItemObject} from "../../../api/types/ancillary";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";

const OWAncillaryExceptionBaseDefinition = ({
                                                originalCommercialSegment,
                                                owAncillaryExceptionInUse,
                                                readOnly,
                                                handleSubmit,
                                                owAncillaryExceptionId,
                                            }: {
    originalCommercialSegment: SelectElementModelFE | null;
    owAncillaryExceptionInUse: boolean;
    readOnly: boolean;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    owAncillaryExceptionId?: number;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["tranlsation, pricing"]);

    // Use Navigate
    const navigate = useNavigate();

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Recoil
    const [commericalCategoryList, setCommercialCategoryList] = useRecoilState(
        commercialCategoryListAtom
    );
    const [commericalSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [chargingWayList] = useRecoilState(chargingWayAtom);
    const [unitsEnum] = useRecoilState(unitsEnumAtom);
    const [serviceItem, setServiceItem] = useState<any>([]);

    // Use State
    const [initialValues] =
        useState<OWAncillaryExceptionBaseDefinitionFormValues>(
            initialOWAncillaryExceptionBaseDefinitionValues
        );
    const [owAncillaryList, setOWAncillaryList] = useState<AncillarySelectFE[]>(
        []
    );

    // Use Repository Hooks
    const {getCommercialCategoriesByCommercialSegmentId} =
        useCommercialCategoryRepository();
    const {getOWAncillariesPaged} = useOWAncillaryRepository();
    const {getServiceItem} = useAncillaryRepository();

    // Use Form Context
    const {watch, formState, getValues, setValue, register, reset} =
        useFormContext<OWAncillaryExceptionBaseDefinitionFormValues>();
    const watchAllFields = watch();
    const watchedLegalEntity = watch("legalEntity");

    // State per categorie originali (non filtrate per label)
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Effect per filtrare le categorie quando cambia la legal entity
    useEffect(() => {
        const legalEntityCode = ((watchedLegalEntity as SelectElementModelFE)?.code as string) 
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);
        
        if (!legalEntityCode || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        commercialCategoryListOriginal.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === legalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [watchedLegalEntity, commercialCategoryListOriginal, user.legalEntities]);

    // Use Effect
    useEffect(() => {
        const getContent = async () => {
            if (originalCommercialSegment) {
                const commercialCategoriesData =
                    await getCommercialCategoriesByCommercialSegmentId(
                        (originalCommercialSegment as SelectElementModelFE).code as string
                    );
                const commercialCategoryList: CommercialCategorySelectFE[] =
                    fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryListOriginal(commercialCategoryList);
            }
        };
        getContent();
    }, [originalCommercialSegment]);

    const handleChangeCommercialSegment = async (
        value: CommercialSegmentSelectFE
    ) => {
        if (value) {
            setValue("commercialSegment", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as CommercialSegmentSelectFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryListOriginal(commercialCategoryList);

            const owAncillariesData: OWAncillaryEntityResponse[] = (
                await getOWAncillariesPaged({
                    commercialSegmentId: value.code as number,
                    showOnlyActive: true,
                    dealers: dealerFilteredOptions,
                    legalEntities: everyLegalEntitiesWithoutAll,
                })
            ).content;
            const owAncillaryList: AncillarySelectFE[] =
                fromOWAncillaryBeToFe(owAncillariesData);
            setOWAncillaryList(owAncillaryList);

            reset({...initialValues, commercialSegment: value});
        } else {
            setValue("commercialSegment", value);
            setValue("commercialCategory", null);
            setCommercialCategoryListOriginal([]);
        }
    };

    const handleChangeOWAncillary = async (value: AncillarySelectFE) => {
        //FIX FORM SET VALUE
        if (value) {
            setValue("prcOWAncillary", value);
            setValue("nameAncillary", value.nameAncillary);
            ancillaryExceptionSetFormValues(setValue, value);
            setValue("code", value.ancillaryCode as string);
            setValue("description", value.description);
            setValue("tax", value.tax);
            setValue("type", value.type);
            setValue("subtype", value.subtype);
            setValue("cardinality", value.cardinality);
            setValue("sellDesk", value.sellDesk);
            setValue("applicationTiming", value.applicationTiming);
            setValue("toleranceTime", value.toleranceTime);
            setValue("additionalField", value.additionalField);
            setValue("taxCode", serviceItem.find((s: ServiceItemObject) => s.code === value.taxCode)?.name || value.taxCode);
            setValue("legalEntity", {
                code: value.legalEntity as string,
                label: t('translation:legalEntities.'.concat(value.legalEntity as string))
            })
            setValue("dealer", {
                code: value.dealer as string,
                label: t('translation:dealers.'.concat(value.dealer as string))
            })

            if (serviceItem.length === 0) {
                const services = await getServiceItem(value.legalEntity as string, value.dealer as string) as unknown as ServiceItemObject[];
                setServiceItem(services);
                setValue("taxCode", services.find((s: ServiceItemObject) => s.code === value.taxCode)?.name || "");
            }

        }

    };

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && owAncillaryExceptionInUse && (
                <CustomBanner
                    message={t("pricing:ancillaryException.banner")}
                    severity={"warning"}
                />
            )}
            <Grid item xs={12} sm={12} md={12}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} py={2}>
                            {/* COMMERCIAL SEGMENT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("commercialSegment")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.commercialSegment"
                                    )}*`}
                                    options={commericalSegmentList || []}
                                    handleChange={handleChangeCommercialSegment}
                                    isOptionEqualToValue={(
                                        option: SelectElementModelFE,
                                        value: SelectElementModelFE
                                    ) => option?.code === value?.code}
                                    readOnly={!!getValues().id || formState.isSubmitting}
                                />
                            </Grid>
                            {/* ANCILLARY*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("prcOWAncillary")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.ancillary"
                                    )}*`}
                                    options={owAncillaryList || []}
                                    handleChange={handleChangeOWAncillary}
                                    readOnly={!!getValues().id || formState.isSubmitting}
                                    isOptionEqualToValue={(option, value) =>
                                        option.code === value.code
                                    }
                                />
                            </Grid>
                            {/*LEGAL ENTITY*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("legalEntity")}
                                    label={`${t("pricing:dealer.tenant")}`}
                                    options={[]}
                                    readOnly={true}
                                />
                            </Grid>
                            {/*DEALER*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register(`dealer`)}
                                    label={`${t("pricing:dealer.dealer")}`}
                                    options={[]}
                                    readOnly={true}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} py={2}>
                            {/* CODE */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("code")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.code")}`}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("nameAncillary")}
                                    label={`${t("pricing:ancillary.form.labels.nameAncillary")}`}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("type")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.type")}`}
                                    options={[]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                    readOnly={true}
                                />
                            </Grid>
                            {/* SUBTYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("subtype")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.subtype"
                                    )}`}
                                    options={[]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                    readOnly={true}
                                />
                            </Grid>
                            {/* APPLICATION TIMING */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("applicationTiming")}
                                    label={t(
                                        "pricing:ancillaryLevelOne.form.labels.applicationTiming"
                                    )}
                                    readOnly={true}
                                    options={["PICK UP", "DROP OFF"]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* TOLERANCE TIME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("toleranceTime")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.toleranceTime"
                                    )}*`}
                                    readOnly={true}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "toleranceTime",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CARDINALITY */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("cardinality")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.cardinality"
                                    )}*`}
                                    readOnly={true}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "cardinality",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* UNIT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("tpUnit")}
                                    label={`${t("pricing:ancillary.form.labels.unit")}`}
                                    readOnly={true}
                                    options={unitsEnum || []}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* RESERVATION FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationFrom")}
                                    label={`${t(
                                        "pricing:ancillary.form.labels.reservationFrom"
                                    )}*`}
                                    readOnly={
                                        readOnly ||
                                        formState.isSubmitting ||
                                        owAncillaryExceptionInUse
                                    }
                                    maxDate={watchAllFields.reservationTo || undefined}
                                />
                            </Grid>
                            {/* RESERVATION TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationTo")}
                                    label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.reservationFrom || undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupFrom")}
                                    label={`${t(
                                        "pricing:ancillary.form.labels.pickupDateFrom"
                                    )}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    maxDate={watchAllFields.pickupTo || undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupTo")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.pickupFrom || undefined}
                                />
                            </Grid>
                            {/* ADDITIONAL FIELD */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("additionalField")}
                                    label={t(
                                        "pricing:ancillaryLevelOne.form.labels.additionalFieldType"
                                    )}
                                    readOnly={true}
                                    options={["NUMBER", "STRING"]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* TAX */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("tax")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.tax")}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* CURRENCY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("currency")}
                                    label={`${t("pricing:ancillary.form.labels.currency")}`}
                                    readOnly={true}
                                    options={[]}
                                />
                            </Grid>
                            {/* PROVIDER */}
                            {/*<Grid item xs={ 12 } md={ 3 }>*/}
                            {/*    <FormAutocomplete*/}
                            {/*        { ...register("provider") }*/}
                            {/*        label={ `${ t("pricing:ancillary.form.labels.provider") }*` }*/}
                            {/*        readOnly={ readOnly || formState.isSubmitting }*/}
                            {/*        options={ providers || [] }*/}
                            {/*        isOptionEqualToValue={ (option, value) => option?.code === value?.code }*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/* MINIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("amountMin")}
                                    label={`${t("pricing:ancillary.form.labels.minimumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "amountMin",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* MAXIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("amountMax")}
                                    label={`${t("pricing:ancillary.form.labels.maximumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "amountMax",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CHARGING WAY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargingWay")}
                                    label={`${t("pricing:ancillary.form.labels.chargingWay")}`}
                                    readOnly={true}
                                    options={chargingWayList}
                                />
                            </Grid>
                            {/* MAX UNIT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maxUnits")}
                                    label={`${t("pricing:ancillary.form.labels.maxUnits")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "maxUnits",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CHARGE TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargeType")}
                                    label={`${t("pricing:ancillary.form.labels.chargeType")}`}
                                    readOnly={true}
                                    options={["VALUE", "PERCENTAGE"]}
                                />
                            </Grid>
                            {/* SELL DESK */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("sellDesk")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.sellDesk")}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* TAXCODE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("taxCode")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.taxCode"
                                    )}`}
                                    readOnly={true}
                                    options={serviceItem.map((item : any) => ({
                                        label: item.name,
                                        value: item.code,
                                    }))}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} py={2}>
                            {/* EXCEPTION NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("name")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.exceptionName"
                                    )}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* DESCRIPTION */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("description")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.description"
                                    )}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* COMMERCIAL CATEGORY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("commercialCategory")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.commercialCategory"
                                    )}*`}
                                    options={commericalCategoryList || []}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.code === value?.code
                                    }
                                    readOnly={
                                        readOnly ||
                                        formState.isSubmitting ||
                                        !watchAllFields.commercialSegment
                                    }
                                />
                            </Grid>
                            {/*<Grid item xs={ 12 } md={ 12 } sx={ { display: "flex", alignItems: "flex-end" } }>*/}
                            {/*    <RadioGroupComponent*/}
                            {/*        { ...register("discountIncreaseSelected") }*/}
                            {/*        value={ discountIncreaseSelected }*/}
                            {/*        handleChange={ handleChangeRadioGroup }*/}
                            {/*        options={ radioButtonOptions }*/}
                            {/*        disabled={ !!getValues().id || readOnly || formState.isSubmitting }*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/* DISCOUNT INCREASE PERCENTAGE */}
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    {...register("discountIncreasePercentage")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.discountIncreasePercentage"
                                    )}`}
                                    readOnly={
                                        !!getValues().id || readOnly || formState.isSubmitting
                                    }
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "discountIncreasePercentage",
                                            numberIntegerWithNegativeValuesRegex
                                        );
                                    }}
                                    startAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            </Grid>
                            {/* DISCOUNT INCREASE FIXED */}
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    {...register("discountIncreaseFixed")}
                                    label={`${t(
                                        "pricing:ancillaryExceptionCreate.form.labels.discountIncreaseFixed"
                                    )}`}
                                    readOnly={
                                        !!getValues().id || readOnly || formState.isSubmitting
                                    }
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(
                                            i18nConvertNumberWithDecimals(value),
                                            setValue,
                                            "discountIncreaseFixed",
                                            numberWithNegativeValuesRegex
                                        );
                                    }}
                                />
                            </Grid>
                            {/* NOTE */}
                            <Grid item xs={12} md={12}>
                                <FormTextField
                                    {...register("note")}
                                    label={`${t("pricing:ancillary.form.labels.notes")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {!readOnly && (
                <Grid
                    container
                    item
                    xs={12}
                    justifyContent="flex-end"
                    columnGap={2}
                >
                    <Grid>
                        <Button
                            variant="outlined"
                            sx={{marginRight: "15px"}}
                            color={"accent"}
                            type={"button"}
                            disabled={formState.isSubmitting}
                            onClick={() => window.close()}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button
                            color={"accent"}
                            type={"button"}
                            onClick={handleSubmit}
                            sx={{border: `1px solid ${theme.palette.accent.main}`}}
                            disabled={formState.isSubmitting}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default OWAncillaryExceptionBaseDefinition;
